import { Button, Divider, Input, Modal, Row, Select, Space, Typography } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  AlignLeftOutlined,
  PlusOutlined,
  CompressOutlined,
  CloseOutlined,
  QuestionOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import './reviewQuestions.less';
import { QueryBuilder } from '../../../../components/molecules/QueryBuilder/QueryBuilder';
import {
  DataTable,
  DataTablePagination,
} from '../../../../components/molecules/DataTable/DataTable';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import { useConnection } from '../../../../connection/Application';
import { Criteria } from './reviewQuestions';
import ReviewPreview from '../../../../components/molecules/ReviewPreview/ReviewPreview';
import Link from 'antd/lib/typography/Link';
import { PageHeader } from '@ant-design/pro-layout';
const { Text } = Typography;
const { confirm } = Modal;
const { TextArea } = Input;

export const ReviewQuestionSets: FC = () => {
  const [questionSetModalVisible, setQuestionSetModalVisible] = useState<boolean>(false);
  const [selectSearch, setSelectSearch] = useState<any | null>();
  const [criteriaArr, setCriteriaArr] = useState<Criteria[]>([]);
  const [criteriaData, setCriteriaData] = useState<Criteria[]>([]);
  const [name, setName] = useState<string>();
  const [query, setQuery] = useState<any>({});
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [selectedCriteriaSet, setSelectedCriteriaSet] = useState<any>();
  const connection = useConnection();
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const [filteredOptions, setFilteredOptions] = useState<Criteria[]>(criteriaData);
  const [editable, setEditable] = useState<boolean>(false);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      // eslint-disable-next-line react/display-name
      render: (name: Text, record: any) => (
        <Link
          onClick={() => {
            setSelectedCriteriaSet(record);
            setName(record.name);
            setCriteriaArr(record.criterias);
            setFilteredOptions(
              criteriaData.filter(
                (o) => !record.criterias.map((x: Criteria) => x.name).includes(o.name),
              ),
            );
            setQuestionSetModalVisible(true);
          }}>
          {name}
        </Link>
      ),
    },
  ];

  useEffect(() => {
    connection
      .get('criteria')
      .then((res) => setCriteriaData(res.data.map((e: any) => ({ ...e, key: e.id }))))
      .catch((e) => console.log(e));
  }, [connection]);

  const search = useCallback(
    (query: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('criteria/criteriaSet', { ...pagination, ...query })
        .then((value) => {
          setData(value.data.map((e: any) => ({ ...e, key: e.id })));
          setTotal(value.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  const createCriteriaSet = useCallback(() => {
    setFetching(true);
    connection
      .post('criteria/criteriaSet', { name, criteriaIds: criteriaArr.map((item) => item.id) })
      .then(() => {
        search(query, pagination);
        setName(undefined);
        setCriteriaArr([]);
        setQuestionSetModalVisible(false);
        setFetching(false);
      })
      .catch((e) => {
        setFetching(false);
        console.log(e);
      });
  }, [connection, criteriaArr, name, pagination, query, search]);

  useEffect(() => {
    search({}, { page: 0, size: 10 });
  }, [search]);

  const editCriteriaSet = useCallback(() => {
    const criterias = criteriaArr.map((item) => item.id);
    const criteriaSet = { name, criterias };
    connection
      .patch(`criteria/criteriaSet/${selectedCriteriaSet?.id}`, { criteriaSet })
      .then(() => {
        setEditable(false);
        search(query, pagination);
      })
      .catch((e) => console.log(e));
  }, [connection, criteriaArr, name, pagination, query, search, selectedCriteriaSet?.id]);

  const deleteCriteriaSet = useCallback(() => {
    connection
      .delete(`criteria/criteriaSet/${selectedCriteriaSet?.id}`)
      .then(() => {
        setEditable(false);
        setSelectedCriteriaSet(undefined);
        setQuestionSetModalVisible(false);
        search(query, pagination);
      })
      .catch((e) => console.log(e));
  }, [connection, pagination, query, search, selectedCriteriaSet?.id]);

  const showDeleteConfirm = useCallback(() => {
    confirm({
      title: `Deleting Criteria Set !`,
      icon: <CloseCircleOutlined />,
      content: `Are you sure delete this criteria Set?`,
      okText: 'Yes,Delete!',
      centered: true,
      okType: 'primary',
      cancelText: 'Cancel',
      onOk() {
        deleteCriteriaSet();
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [deleteCriteriaSet]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Criteria Set"
        subTitle="Manage criteria set details"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginTop: '1%',
              width: '100%',
            }}>
            <QueryBuilder
              searchPlaceholder="Search Review Set"
              search={true}
              value={query}
              onChange={setQuery}
              onSearch={(value) => search(value, pagination)}
              schema={[]}
            />
            <Space>
              <Button
                onClick={() => {
                  setQuery(undefined);
                  search(undefined, { page: 1, size: pagination.size });
                }}>
                Reset
              </Button>
              <Button type="primary" onClick={() => search(query, pagination)}>
                Search
              </Button>
            </Space>
          </div>
        </WebUxPageItem>
        <WebUxPageItem>
          <Row
            style={{
              alignItems: 'baseline',
              marginTop: '1%',
              justifyContent: 'flex-end',
              marginBottom: '0.5%',
            }}>
            <Button
              type="primary"
              onClick={() => {
                setFilteredOptions(criteriaData);
                setQuestionSetModalVisible(true);
              }}>
              <PlusOutlined style={{ fontSize: '100%', color: 'white' }} />
              New Criteria Set
            </Button>
          </Row>
          <DataTable
            columns={columns}
            total={total}
            loading={loading}
            dataSource={data}
            onPaginationChange={(p) => {
              setPagination(p);
              if (pagination.page !== p.page || pagination.size !== p.size) {
                search(query, p);
              }
            }}
            dtPagination={pagination}
            style={{ marginTop: '1%' }}
          />
        </WebUxPageItem>
      </WebUxPageContent>

      <Modal
        title={selectedCriteriaSet ? name : 'Add a Criteria Set'}
        destroyOnClose
        footer={
          selectedCriteriaSet ? (
            <Row style={{ justifyContent: 'space-between', margin: '0 1% 0 1%' }}>
              <Button onClick={() => showDeleteConfirm()} danger>
                Delete
              </Button>
              <Space>
                {editable ? (
                  <Button type="default" onClick={() => setEditable(false)}>
                    Cancel
                  </Button>
                ) : (
                  <Button
                    type="default"
                    onClick={() => {
                      setEditable(false);
                      setSelectedCriteriaSet(undefined);
                      setQuestionSetModalVisible(false);
                    }}>
                    Close
                  </Button>
                )}
                {editable ? (
                  <Button type="primary" onClick={() => editCriteriaSet()}>
                    Save
                  </Button>
                ) : (
                  <Button type="primary" onClick={() => setEditable(true)}>
                    Edit
                  </Button>
                )}
              </Space>
            </Row>
          ) : (
            <>
              <Button
                type="default"
                onClick={() => {
                  setSelectedCriteriaSet(undefined);
                  setEditable(false);
                  setName(undefined);
                  setCriteriaArr([]);
                  setQuestionSetModalVisible(false);
                  setFilteredOptions(criteriaData);
                }}>
                Cancel
              </Button>
              <Button loading={fetching} type="primary" onClick={() => createCriteriaSet()}>
                Save
              </Button>
            </>
          )
        }
        open={questionSetModalVisible}
        onCancel={() => {
          setSelectedCriteriaSet(undefined);
          setEditable(false);
          setName(undefined);
          setCriteriaArr([]);
          setQuestionSetModalVisible(false);
          setFilteredOptions(criteriaData);
        }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Space>
            <Space style={{ width: 100 }}>
              <CompressOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Name</Text>
            </Space>
            <div style={{ width: 400 }}>
              {!editable && selectedCriteriaSet ? (
                <Text>{name}</Text>
              ) : (
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  allowClear
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                />
              )}
            </div>
          </Space>
          <Space style={{ marginTop: '5%' }}>
            <Space>
              {!editable && selectedCriteriaSet ? (
                <QuestionOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              ) : (
                <PlusOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              )}

              <Text type="secondary">
                {!editable && selectedCriteriaSet ? 'Criterias' : 'Add Criterias'}
              </Text>
            </Space>
          </Space>
          <Divider orientation="left" style={{ marginTop: 0, marginBottom: 0 }} />
          {!editable && selectedCriteriaSet ? (
            <div />
          ) : (
            <Select
              showSearch
              placeholder="Type the criteria you want to add"
              style={{ marginTop: '2%', marginBottom: '2%' }}
              onChange={(value) => {
                const item = criteriaData.find((criteria) => criteria.id === value);
                if (item) setCriteriaArr([...criteriaArr, item]);
                setSelectSearch(null);
              }}
              filterOption={(input, option) =>
                option?.props?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={selectSearch}>
              {filteredOptions.map((item, key) => (
                <Select.Option key={key} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          )}
          {criteriaArr &&
            criteriaArr.map((item, key) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  border: '1px solid rgba(56,54,48,.16)',
                  marginTop: 5,
                  padding: 5,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                <Text>{item.name}</Text>
                {!editable && selectedCriteriaSet ? (
                  <div />
                ) : (
                  <CloseOutlined
                    onClick={() => {
                      setFilteredOptions(
                        criteriaData.filter(
                          (o) =>
                            !criteriaArr
                              .filter((criteria) => criteria.id !== item.id)
                              .map((x: Criteria) => x.name)
                              .includes(o.name),
                        ),
                      );
                      setCriteriaArr(criteriaArr.filter((criteria) => criteria.id !== item.id));
                    }}
                  />
                )}
              </div>
            ))}
          {name && criteriaArr.length > 0 && (
            <>
              <div style={{ marginTop: '2%' }}>
                <Space style={{ width: 100 }}>
                  <AlignLeftOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                  <Text type="secondary">Preview</Text>
                </Space>
              </div>
              <div className="borderBox">
                <ReviewPreview mode={'edit'} criterias={criteriaArr} />
              </div>
            </>
          )}
        </div>
      </Modal>
    </WebUxPage>
  );
};
